import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../utils/analytics';
import { getCurrentUser } from '../utils/roleLabel';
import { DELIVERY_ENABLED } from '../utils/features';

/**
 * OnboardingGoalsModal
 * ─────────────────────────────────────────────────────────────────
 * Shown the first few dashboard visits for a new account. Asks the
 * leader what they want to achieve so the platform can orient them
 * (and so we can analyse the spread of intent across users).
 *
 * Behaviour:
 *   - Renders only on /dashboard.
 *   - Counter increments each time the modal would otherwise show;
 *     after MAX_SHOWS visits it stops appearing unless the user
 *     explicitly picks a goal earlier.
 *   - "Save" stores the choice + stops the modal forever.
 *   - "Skip for now" hides it for this session only; counter ticks.
 *   - "Don't show again" sets the permanent-dismiss flag.
 *
 * Choices persist as { guruone_onboarding_goal: { choice, custom,
 * decidedAt } } in localStorage. Counter + dismiss flag are
 * separate keys.
 */

const MAX_SHOWS = 5;

const GOAL_OPTIONS = [
  { id: 'structure_strategy', label: 'Structure my direction', hint: 'Articulate purpose, values, mission, vision, customers, positioning.' },
  { id: 'design_business',    label: 'Design how my business runs', hint: 'Map operating model — people, process, finance, suppliers, governance.' },
  { id: 'work_through_decision', label: 'Work through a specific decision', hint: 'Use Decision Guru to structure a choice and pressure-test options.' },
  // Hidden on prod while Delivery Guru is gated off (REACT_APP_DELIVERY_ENABLED).
  ...(DELIVERY_ENABLED ? [{ id: 'plan_execution', label: 'Plan execution & delivery', hint: 'Turn your direction into a concrete delivery plan.' }] : []),
  { id: 'explore_guruone',    label: 'Just exploring what GuruOne does', hint: 'Look around, see how it feels, decide what to use it for.' },
];

function userKey(suffix) {
  try {
    const u = getCurrentUser();
    const uid = u?.id ? `user_${u.id}` : 'default';
    return `guruone_onboarding_${suffix}_${uid}`;
  } catch {
    return `guruone_onboarding_${suffix}_default`;
  }
}

function readCount() {
  try {
    const v = parseInt(localStorage.getItem(userKey('shown_count')), 10);
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}
function writeCount(n) {
  try { localStorage.setItem(userKey('shown_count'), String(n)); } catch { /* noop */ }
}
function readDismissed() {
  try { return localStorage.getItem(userKey('dismissed_perm')) === '1'; } catch { return false; }
}
function writeDismissed() {
  try { localStorage.setItem(userKey('dismissed_perm'), '1'); } catch { /* noop */ }
}
function readGoal() {
  try {
    const raw = localStorage.getItem(userKey('goal'));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeGoal(payload) {
  try {
    localStorage.setItem(userKey('goal'), JSON.stringify({ ...payload, decidedAt: new Date().toISOString() }));
  } catch { /* noop */ }
}

export default function OnboardingGoalsModal({ pathname }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (pathname !== '/dashboard') { setVisible(false); return; }
    // Don't show if user already picked, dismissed permanently, or
    // exceeded the soft cap on visits.
    if (readGoal() || readDismissed()) { setVisible(false); return; }
    const count = readCount();
    if (count >= MAX_SHOWS) { setVisible(false); return; }
    writeCount(count + 1);
    setVisible(true);
    track('onboarding_goals_shown', { visit: count + 1 });
  }, [pathname]);

  if (!visible) return null;

  function handleSave() {
    if (!selected) return;
    const payload = { choice: selected };
    writeGoal(payload);
    track('onboarding_goals_saved', { choice: payload.choice });
    setVisible(false);

    // Light-touch nudge toward the most relevant starting place.
    const ROUTE_BY_GOAL = {
      structure_strategy: '/strategy',
      design_business: '/operating-model',
      work_through_decision: '/decision',
      plan_execution: '/delivery',
    };
    const dest = ROUTE_BY_GOAL[selected];
    if (dest) {
      // Tiny delay so the modal can unmount before the route change.
      setTimeout(() => navigate(dest), 100);
    }
  }
  function handleSkip() {
    track('onboarding_goals_skipped');
    setVisible(false);
  }
  function handleDontShowAgain() {
    writeDismissed();
    track('onboarding_goals_dismissed_perm');
    setVisible(false);
  }

  const visits = readCount();
  return (
    <div role="dialog" aria-modal="true" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: 'white', borderRadius: 14,
        maxWidth: 560, width: '100%', maxHeight: '90vh',
        boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '1.25rem 1.5rem 0.85rem', borderBottom: '1px solid #f0f0f0',
        }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', color: '#aaa', textTransform: 'uppercase' }}>Welcome</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>
            What would you most like to achieve with GuruOne?
          </div>
          <div style={{ fontSize: '0.82rem', color: '#666', marginTop: 6, lineHeight: 1.55 }}>
            Pick one — we'll point you at the right starting place. You can do everything else any time. {visits > 1 && `(asking again ${visits}/${MAX_SHOWS})`}
          </div>
        </div>

        <div style={{ padding: '1rem 1.5rem', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {GOAL_OPTIONS.map(opt => {
              const isSel = selected === opt.id;
              return (
                <button key={opt.id} type="button" onClick={() => setSelected(opt.id)}
                  style={{
                    textAlign: 'left',
                    padding: '0.75rem 0.95rem',
                    borderRadius: 10,
                    border: isSel ? '2px solid #1D9E75' : '1.5px solid #e5e7eb',
                    background: isSel ? '#f0faf6' : 'white',
                    cursor: 'pointer', fontFamily: 'inherit', width: '100%',
                  }}>
                  <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#1a1a2e' }}>
                    {opt.label}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 3, lineHeight: 1.5 }}>
                    {opt.hint}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{
          padding: '0.9rem 1.5rem', borderTop: '1px solid #f0f0f0',
          display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button onClick={handleDontShowAgain}
            style={{
              background: 'none', color: '#888', border: 'none',
              fontSize: '0.78rem', cursor: 'pointer', padding: '0.45rem 0', fontFamily: 'inherit',
              width: 'auto', textDecoration: 'underline',
            }}>
            Don't show this again
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSkip}
              style={{
                background: 'white', color: '#444', border: '1px solid #e0e0e0',
                borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.85rem',
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              Skip for now
            </button>
            <button onClick={handleSave}
              disabled={!selected}
              style={{
                background: !selected ? '#ccc' : '#1D9E75',
                color: 'white', border: 'none', borderRadius: 8,
                padding: '0.6rem 1.2rem', fontSize: '0.88rem', fontWeight: 600,
                cursor: !selected ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', width: 'auto',
              }}>
              Take me there →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
